import { useQuery } from "@apollo/client";
import { useCallback, useMemo, useState } from "react";
import { listKeywordGroups } from "../graphql/queries/listKeywordGroup";
import { MESSAGES } from "../pages/management/KeywordManagement/constants";

export const useKeywords = () => {
  const [localKeywords, setLocalKeywords] = useState([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchCategories, setSearchCategories] = useState([]);
  const [nextId, setNextId] = useState(1);

  const { data, loading, error, refetch } = useQuery(listKeywordGroups, {
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  const transformedKeywords = useMemo(() => {
    if (!data?.listKeywordGroups) return [];

    const keywords = data.listKeywordGroups
      .map((group) => ({
        id: group.id,
        name: group.name,
        slug: group.slug,
        type: group.type,
        deleted: group.deleted,
        imageUrl: group.imageUrl,
        keywords: group.keywords,
        category: group.category,
        metadata: group.metadata,
        // Pre-compute lowercase name for efficient filtering
        _searchName: group.name.toLowerCase(),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));

    const maxId = keywords.length > 0 ? Math.max(...keywords.map((k) => k.id)) : 0;
    setNextId(maxId + 1);

    return keywords;
  }, [data]);

  const allKeywords = useMemo(() => {
    return [...transformedKeywords, ...localKeywords];
  }, [transformedKeywords, localKeywords]);

  const categories = useMemo(() => {
    return [...new Set(allKeywords.map((keyword) => keyword.category))]
      .filter((c) => !!c)
      .map((category) => ({
        id: category,
        displayText: category,
      }));
  }, [allKeywords]);

  const keywords = useMemo(() => {
    const searchLower = searchTerm.toLowerCase().trim();
    return allKeywords
      .filter((keyword) => !searchLower || keyword._searchName.includes(searchLower))
      .filter(
        (keyword) =>
          !searchCategories.length ||
          searchCategories.map((category) => category.id).includes(keyword.category)
      );
  }, [allKeywords, searchTerm, searchCategories]);

  const handleEdit = useCallback((keyword) => {
    alert(MESSAGES.EDIT_CONFIRMATION(keyword.name));
  }, []);

  const handleSoftDelete = useCallback((keywordId) => {
    if (window.confirm(MESSAGES.DELETE_CONFIRMATION)) {
      setLocalLoading(true);

      setTimeout(() => {
        setLocalKeywords((prevKeywords) =>
          prevKeywords.map((keyword) =>
            keyword.id === keywordId ? { ...keyword, deleted: true } : keyword
          )
        );
        setLocalLoading(false);
      }, 500);
    }
  }, []);

  const handleAddKeyword = useCallback(() => {
    const timestamp = Date.now();
    const newKeyword = {
      id: nextId,
      name: `New Keyword ${timestamp}`,
      slug: `new-keyword-${timestamp}`,
      type: "New",
      deleted: false,
      _searchName: `new keyword ${timestamp}`,
    };
    setLocalKeywords((prev) => [...prev, newKeyword]);
    setNextId((prev) => prev + 1);
  }, [nextId]);

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleSearch = useCallback((searchValue) => {
    setSearchTerm(searchValue || "");
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleSearchCategory = useCallback((selectedCategories) => {
    setSearchCategories(selectedCategories);
  }, []);

  return {
    keywords,
    categories,
    loading: loading || localLoading,
    error,
    searchTerm,
    searchCategories,
    refetch,
    handlers: {
      handleEdit,
      handleSoftDelete,
      handleAddKeyword,
      handleRefresh,
      handleSearch,
      handleSearchChange,
      handleSearchCategory,
    },
  };
};
